import * as React from 'react';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {EmptyAddCard} from 'shells/card/empty-add-card';
import {LeadChevronRegularIcon} from 'shells/icon';
import {DripsRegularIcon} from 'shells/icon/svg/drips-regular-icon';
import {PeopleRegularIcon} from 'shells/icon/svg/people-regular-icon';
import {TagRegularDiagonalIcon} from 'shells/icon/svg/tag-regular-diagonal-icon';
import {Stack} from 'shells/layout/stack/stack';

import type {ActionType} from 'nutshell-graphql-types';
import type {
    GetMoneyPage_maUrl_automation_actions as AutomationAction,
    ActionInput,
} from 'nutshell-graphql-types';

import {CreateEditAutomationSidebarForm} from '../create-edit-automation-sidebar-form';
import {useCreateMoneyPageAutomation} from '../graphql/hooks/use-create-money-page-automation';
import {useDeleteMoneyPageAction} from '../graphql/hooks/use-delete-money-page-action';
import {useUpdateMoneyPageAutomation} from '../graphql/hooks/use-update-money-page-automation';

import {AutomationCard} from './automation-card';
import {getSubtitleForAction} from './helpers';

type Props = {
    actions: AutomationAction[];
    urlId: string;
    progressTour: () => void;
};

const ACTION_CARD_CONFIG: Partial<
    Record<
        ActionType,
        {
            title: string;
            icon: React.ComponentType<any>;
        }
    >
> = {
    ADD_TAGS: {
        title: 'Add a tag',
        icon: TagRegularDiagonalIcon,
    },
    ADD_AUDIENCES: {
        title: 'Add to an audience',
        icon: PeopleRegularIcon,
    },
    REMOVE_AUDIENCES: {
        title: 'Remove from an audience',
        icon: PeopleRegularIcon,
    },
    ADD_TO_DRIP_SEQUENCE: {
        title: 'Add to a drip sequence',
        icon: DripsRegularIcon,
    },
    CREATE_LEAD: {
        title: 'Create a new lead',
        icon: LeadChevronRegularIcon,
    },
};

export function CreateCardContainer(props: Props) {
    const configuredTypesSet: Set<ActionType> = new Set();
    const [showCreateEditAutomationSidebar, setShowCreateEditAutomationSidebar] =
        React.useState<boolean>(false);
    const [existingAutomationAction, setExistingAutomationAction] = React.useState<
        AutomationAction | null | undefined
    >(null);
    const [errorMessage, setErrorMessage] = React.useState<string | null | undefined>(null);

    const {createMoneyPageAutomation, isLoading: isSaving} = useCreateMoneyPageAutomation();

    const {updateMoneyPageAutomation, isLoading: isUpdating} = useUpdateMoneyPageAutomation();

    const {deleteMoneyPageAction, isLoading: isDeleting} = useDeleteMoneyPageAction();

    React.useEffect(() => {
        if (showCreateEditAutomationSidebar && !existingAutomationAction) {
            const timer = setTimeout(() => {
                props.progressTour();
            }, 100);

            return () => clearTimeout(timer);
        }
    }, [showCreateEditAutomationSidebar, existingAutomationAction, props]);

    const handleDelete = async (automationActionToDelete: AutomationAction) => {
        await deleteMoneyPageAction({
            actionId: automationActionToDelete.id,
        });
        setShowCreateEditAutomationSidebar(false);
    };

    const handleSave = (
        automationActionToSave: ActionInput,
        newType: ActionType | null | undefined
    ) => {
        const updateOrCreatePromise = existingAutomationAction
            ? updateMoneyPageAutomation({
                  actionId: existingAutomationAction.id,
                  config: automationActionToSave.config,
                  type: newType,
              })
            : createMoneyPageAutomation({
                  urlId: props.urlId,
                  action: automationActionToSave,
              });

        updateOrCreatePromise
            .then(() => {
                setShowCreateEditAutomationSidebar(false);
            })
            .catch((e) => {
                setErrorMessage(safelyGetGraphQLErrorMessage(e) || 'Failed to save');
            });
    };

    return (
        <Stack spacing={8}>
            {props.actions.map((action) => {
                configuredTypesSet.add(action.type);

                const config = ACTION_CARD_CONFIG[action.type];

                if (!config) {
                    return null;
                }

                return (
                    <AutomationCard
                        key={action.id}
                        icon={config.icon}
                        title={config.title}
                        subtitle={getSubtitleForAction(action)}
                        onClick={() => {
                            setShowCreateEditAutomationSidebar(true);
                            setExistingAutomationAction(action);
                            setErrorMessage(null);
                        }}
                    />
                );
            })}
            {configuredTypesSet.size < Object.keys(ACTION_CARD_CONFIG).length && (
                <EmptyAddCard
                    onClick={() => {
                        setShowCreateEditAutomationSidebar(true);
                        setExistingAutomationAction(null);
                        setErrorMessage(null);
                    }}
                    text='Add an action'
                    background='off-white'
                    size='small'
                    withoutIcon={true}
                    isLoading={false}
                    height={60}
                />
            )}
            {showCreateEditAutomationSidebar ? (
                <CreateEditAutomationSidebarForm
                    configuredTypesSet={configuredTypesSet}
                    existingAutomationAction={existingAutomationAction}
                    onClose={() => {
                        setShowCreateEditAutomationSidebar(false);
                    }}
                    handleSave={handleSave}
                    isSaving={isSaving || isUpdating || isDeleting}
                    // @ts-expect-error
                    onDelete={existingAutomationAction ? handleDelete : undefined}
                    saveFailedErrorMessage={errorMessage}
                />
            ) : undefined}
        </Stack>
    );
}
